<template>
  <!-- main row -->
  <v-row class="ma-0">
    <!-- main col (non mobile version) -->
    <v-col class="pa-0" v-if="!isMobile">
      <v-list
        style="width: 100% !important; border-bottom-style: ridge;"
        class="pa-1 pb-1"
      >
        <!-- border-top-style: ridge -->
        <!-- row for title -->
        <v-row class="ma-0 pa-0">
          <h4 class="font-weight-regular text-wrap px-2">
            {{ product.Name }}
          </h4>
        </v-row>
        <!-- row for img, price, qty and add btn -->
        <v-row class="ma-0" align="center">
          <!-- image -->
          <v-col class="pa-0" cols="3" lg="3" align="center">
            <v-img
              max-height="90px"
              max-width="100px"
              v-if="product.PictureUrl.length > 0"
              :aspect-ratio="1.618"
              :src="product.PictureUrl"
            >
              <!-- sold out text -->
              <template v-if="!hasCapacity(product)">
                <div class="sold-out-label-box"></div>
                <div
                  class="sold-out-label-text"
                  v-text="$translate('PRODUCT_SOLD_OUT')"
                ></div>
              </template>
              <!-- Highlighting label -->
              <HighlightingLabel
                :label="product.HighlightingLabel"
                :visible="product.Highlighted"
              />
            </v-img>
            <v-icon v-else x-large color="secondary">image</v-icon>
          </v-col>
          <!-- price -->
          <v-col class="pa-0" cols="4" lg="3" v-if="showUnitPrice" align="center">
            <!-- Unit price -->
            <div>
              <span v-if="showRetailUnitPrice" class="text-subtitle-2">
                {{ formatter.format(retailUnitPrice) }}
              </span>
              <span
                class="text-subtitle-2"
                :class="hasGeneralDiscount(product) ? 'red--text' : ''"
              >
                {{ formatter.format(unitPrice) }}
              </span>
              <span v-if="isWeightedProduct(product)" class="text-caption">
                /
                <span v-if="product.WeightedInfo.OverviewSize > 1">
                  {{ product.WeightedInfo.OverviewSize }}
                </span>
                {{ product.WeightedInfo.Unit }}
              </span>
              <span class="text-caption text--secondary">
                {{ priceSuffix }}
              </span>
            </div>
            <div v-if="isProfUser">
              <span class="text-subtitle-2">
                {{ formatter.format(totalPriceInclVat) }}
              </span>
              <span class="text-caption text--secondary">
                {{ getVATText(product.VatPercentage) }}
              </span>
            </div>
            <!-- Minimum quantity for Weighted product -->
            <div
              v-if="
                isWeightedProduct(product) &&
                getDisplayMinQuantity(product) > getDisplayStepSize(product)
              "
              class="text-caption text--primary"
            >
              Minimum
              {{ getDisplayMinQuantity(product) }}
              {{ getDisplayUnit(product) }}
            </div>
            <div v-if="product.DepositValue > 0"
                 class="text-caption text--primary py-0 my-0">
              {{ $translate('DEPOSIT_VALUE') }}  {{ formatter.format(product.DepositValue) }}
            </div>
          </v-col>
          <!-- quantity operation -->
          <v-col class="pa-0" cols="5" lg="3" align="center">
            <div>
              <div>
              <span v-if="showRetailUnitPrice" class="discount-price mr-1">
                {{ formatter.format(totalRetailPrice) }}
              </span>
                <span
                  class="text-h6 font-weight-medium"
                  :class="hasGeneralDiscount ? 'red--text' : ''"
                >{{ formatter.format(totalPrice) }}</span
                >
                <span class="text-caption text--secondary">
                {{ priceSuffix }}
              </span>
              </div>
            </div>
            <v-btn-toggle rounded>
              <v-btn
                small
                :disabled="decreaseBtnDisabled"
                @click="decreaseQuantity"
              >
                <v-icon small>ti-minus</v-icon>
              </v-btn>
              <v-card
                height="28"
                min-width="70"
                flat
                outlined
                tile
                class="text-center px-3 text-subtitle-2 font-weight-medium d-flex"
              >
                <div class="mx-auto">
                  {{ getDisplayQuantity(product, quantity) }}
                </div>
                <span v-if="isWeightedProduct" class="ml-1">
                  {{ getDisplayUnit(product) }}
                </span>
              </v-card>
              <v-btn
                small
                :disabled="increaseBtnDisabled"
                @click="increaseQuantity"
              >
                <v-icon small>ti-plus</v-icon>
              </v-btn>
            </v-btn-toggle>

          </v-col>
          <!-- shopping list and Add btn -->
          <v-col class="pa-0 text-center"  cols="12" lg="3">
            <SingleProductOrderAddToList
              :selectedProduct="product"
              :selectedItem="selectedItem"
              :quantity="quantity"
              :backgroundColor="orderBtnBgColor"
              :profView="true"
              @book="onBookBtnClick"
              @login="goToLogin"
            />
          </v-col>
        </v-row>
        <!--   items     -->
        <v-row class="ma-0">
          <v-col cols="12" sm="6" class="pa-0">
            <v-row
              v-if="getSortedItemDefNames(product.ItemDefs).length > 0"
              class="pa-0 ma-0"
            >
              <v-col
                v-for="itemName in getSortedItemDefNames(product.ItemDefs)"
                cols="12"
                md="6"
                class="pa-0"
                :key="itemName"
              >
                <v-select
                  v-model="selectedItemDefs[itemName]"
                  :items="getSortedItemDefValues(product, itemName)"
                  :label="itemName"
                  hide-details="auto"
                  dense
                  filled
                  @input="onItemDefChange(itemName)"
                />
              </v-col>
            </v-row>

            <!-- Out of stock alert -->
            <v-alert
              v-if="product.Items.length > 1 && outOfStock"
              v-model="outOfStock"
              type="error"
              dense
            >
              {{ $translate('OUT_OF_STOCK') }}
            </v-alert>
          </v-col>
        </v-row>
        <!-- Product Availability info -->
        <v-row
          class="ma-0 pa-0"
          v-if="
            product.AvailabilityBatchGroup !== null &&
            product.AvailabilityBatchGroup.BatchGroupId !== 0
          "
        >
          <ProductAvailabilityDetails :item="product">
          </ProductAvailabilityDetails>
        </v-row>
      </v-list>
    </v-col>

    <!-- main col (mobile version) -->
    <v-col class="pa-0" v-else-if="isMobile">
      <v-list
        style="width: 100% !important; border-bottom-style: ridge"
        class="pa-0"
      >
        <!-- row for title -->
        <v-row class="ma-0">
          <v-col class="pa-0 px-1" cols="12">
            <span class="font-weight-normal text-subtitle-1" align="left">
              {{ product.Name }}
            </span>
            <v-badge
              class="pa-0 pl-1"
              :content="getProductCount"
              :value="getProductCount"
              color="secondary"
            ></v-badge>
          </v-col>
        </v-row>
        <!-- row for img, price, qty and add btn -->
        <v-row class="ma-0">
          <!-- image -->
          <v-col class="pa-0 px-1" cols="5">
            <v-img
              max-height="100px"
              max-width="100px"
              v-if="product.PictureUrl.length > 0"
              :aspect-ratio="1.618"
              :src="product.PictureUrl"
            >
              <!-- sold out text -->
              <template v-if="!hasCapacity(product)">
                <div class="sold-out-label-box"></div>
                <div
                  class="sold-out-label-text"
                  v-text="$translate('PRODUCT_SOLD_OUT')"
                ></div>
              </template>
              <!-- Highlighting label -->
              <HighlightingLabel
                :label="product.HighlightingLabel"
                :visible="product.Highlighted"
              />
            </v-img>
            <v-icon v-else x-large color="secondary">image</v-icon>
          </v-col>
          <!-- price -->
          <v-col class="pa-0 " cols="7" v-if="showUnitPrice">
            <v-row class="ma-0">
<!--              <div>-->
                <v-col class="pa-0 text-left">
                  <div>
                  <span v-if="showRetailUnitPrice" class="text-subtitle-2">
                    {{ formatter.format(retailUnitPrice) }}
                  </span>
                    <span
                      class="text-subtitle-2"
                      :class="hasGeneralDiscount(product) ? 'red--text' : ''"
                    >
                    {{ formatter.format(unitPrice) }}
                  </span>
                    <span v-if="isWeightedProduct(product)" class="text-caption">
                    /
                    <span v-if="product.WeightedInfo.OverviewSize > 1">
                      {{ product.WeightedInfo.OverviewSize }}
                    </span>
                    {{ product.WeightedInfo.Unit }}
                  </span>
                    <span class="text-caption text--secondary">
                    {{ priceSuffix }}
                  </span>
                  </div>
                  <div>
                  <span v-if="showRetailUnitPrice" class="discount-price mr-1">
                    {{ formatter.format(totalRetailPrice) }}
                  </span>
                    <span
                      class="text-h6 font-weight-medium"
                      :class="hasGeneralDiscount ? 'red--text' : ''"
                    >{{ formatter.format(totalPrice) }}</span
                    >
                    <span class="text-caption text--secondary">
                    {{ priceSuffix }}
                  </span>
                  </div>
                  <div v-if="isProfUser">
                  <span class="text-subtitle-2 font-weight-regular">
                    {{ formatter.format(totalPriceInclVat) }}
                  </span>
                    <span class="text-caption text--secondary">
                    {{ getVATText(product.VatPercentage) }}
                  </span>
                  </div>
                  <!-- Minimum quantity for Weighted product -->
                  <div
                    v-if="
                    isWeightedProduct(product) &&
                    getDisplayMinQuantity(product) > getDisplayStepSize(product)
                  "
                    class="text-caption text--primary"
                  >
                    Minimum
                    {{ getDisplayMinQuantity(product) }}
                    {{ getDisplayUnit(product) }}
                  </div>
                  <div v-if="product.DepositValue > 0"
                       class="text-caption text--primary py-0 my-0">
                    {{ $translate('DEPOSIT_VALUE') }}  {{ formatter.format(product.DepositValue) }}
                  </div>
                </v-col>
<!--              </div>-->
            </v-row>
          </v-col>
        </v-row>

        <!--  product items -->
        <v-row class="ma-0 mx-2 mt-1">
          <v-col cols="12" sm="6" class="pa-0 pt-1">
            <v-row
              v-if="getSortedItemDefNames(product.ItemDefs).length > 0"
              class="ma-0"
            >
              <v-col
                v-for="itemName in getSortedItemDefNames(product.ItemDefs)"
                cols="12"
                md="6"
                class="pt-0 px-0"
                :key="itemName"
              >
                <v-select
                  v-model="selectedItemDefs[itemName]"
                  :items="getSortedItemDefValues(product, itemName)"
                  :label="itemName"
                  hide-details="auto"
                  dense
                  filled
                  @input="onItemDefChange(itemName)"
                />
              </v-col>
            </v-row>

            <!-- Out of stock alert -->
            <v-alert
              v-if="product.Items.length > 1 && outOfStock"
              v-model="outOfStock"
              type="error"
              dense
            >
              {{ $translate('OUT_OF_STOCK') }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row class="ma-0 mx-1 mb-1">
          <v-col class="pa-0  text-center" cols="8">
            <v-btn-toggle rounded>
                  <v-btn
                    small
                    :disabled="decreaseBtnDisabled"
                    @click="decreaseQuantity"
                  >
                    <v-icon small>ti-minus</v-icon>
                  </v-btn>
                  <v-card
                    height="28"
                    min-width="70"
                    flat
                    outlined
                    tile
                    class="text-center px-3 text-subtitle-2 font-weight-medium d-flex"
                  >
                    <div class="mx-auto">
                      {{ getDisplayQuantity(product, quantity) }}
                    </div>
                    <span v-if="isWeightedProduct" class="ml-1">
                  {{ getDisplayUnit(product) }}
                </span>
                  </v-card>
                  <v-btn
                    small
                    :disabled="increaseBtnDisabled"
                    @click="increaseQuantity"
                  >
                    <v-icon small>ti-plus</v-icon>
                  </v-btn>
                </v-btn-toggle>
          </v-col>
          <!-- shopping list and Add btn -->
          <v-col class="pa-0 text-center" cols="4">
            <SingleProductOrderAddToList
              :selectedProduct="product"
              :selectedItem="selectedItem"
              :quantity="quantity"
              :backgroundColor="orderBtnBgColor"
              :profView="true"
              @book="onBookBtnClick"
              @login="goToLogin"
            />
          </v-col>
        </v-row>
        <!-- Product availability info-->
        <v-row
          class="ma-0 pa-0 pt-2"
          v-if="
            product.AvailabilityBatchGroup !== null &&
            product.AvailabilityBatchGroup.BatchGroupId != 0
          "
        >
          <ProductAvailabilityDetails :item="product">
          </ProductAvailabilityDetails>
        </v-row>
      </v-list>
    </v-col>

    <div>
      <!-- Dialog to check user's age -->
      <ProductAgeAlert
        v-model="showAgeAlert"
        :forAge="product.ForAge"
        @confirm="triggerShoppingListOrCard"
      />
    </div>
  </v-row>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import { SHOPPING_CONTAINER } from '@/shared/constants';
import template from '../../generalComponents/uiComponents/template.vue';
import {
  hasCapacity,
  hasGeneralDiscount,
  isOnDiscount,
  personalDiscount,
  applyPersonalDiscount,
  isWeightedProduct,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  getDisplayMinQuantity,
  getDisplayUnit,
  getDisplayStepSize,
  getDisplayQuantity,
  quantityDecreasable,
  quantityIncreasable,
  removeDefaultItem,
  getQuantity,
  getVATText,
  getTotalPriceInclVAT,
  getTotalPriceExclVAT,
  getTotalRetailPriceInclVAT,
  getTotalRetailPriceExclVAT,
  getSortedItemDefNames,
  getSortedItemDefValues,
} from '@/objects/Product';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';
import ProductPersonalDiscount from '@/components/event/productDetails/ProductPersonalDiscount';
import ProfessionalViewProductAddToCart from '@/components/event/professionalViewType/ProfessionalViewProductAddToCart';
import ProductAgeAlert from '@/components/event/eventDetails/productOrderComponents/ProductAgeAlert';
import utility from '@/shared/utility';
import ProductAvailabilityDetails from '@/components/event/eventDetails/productOrderComponents/ProductAvailabilityDetails';
import SingleProductOrderAddToList from '@/components/event/eventDetails/productOrderComponents/SingleProductOrderAddToList';
import { addProductToReservation } from '@/objects/ShoppingList';

const SHOPPING_CART_FLOW = 'shoppingCartFlow';
const SHOPPING_LIST_FLOW = 'shoppingListFlow';
export default {
  name: 'ProfessionalViewProduct',
  props: {
    item: {
      type: Object,
      default: {},
    },

  },
  data() {
    return {
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      product: this.item,
      selectedFlow: '',
      showAgeAlert: false,

      //order flow
      shoppingListFlow: SHOPPING_LIST_FLOW,
      shoppingCartFlow: SHOPPING_CART_FLOW,
      selectedItem: {},
      selectedItemDefs: {},
      selectedShoppingList: {},
      quantity: 1,
      shoppingCartCounter: [],
      productsInCart: [],
    };
  },
  mounted() {
    this.init();
    this.initOrders();
  },
  watch: {
    orders() {
      this.initOrders();
    },
  },
  components: {
    template,
    HighlightingLabel,
    ProductPersonalDiscount,
    ProfessionalViewProductAddToCart,
    ProductAgeAlert,
    ProductAvailabilityDetails,
    SingleProductOrderAddToList,
  },
  computed: {
    ...mapState('shoppingCart', {
      orders: (state) => state.orders,
    }),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    ...mapGetters({
      isProfUser: 'isProfUser',
    }),
    disableBtn() {
      if (!hasCapacity(this.product)) return true;
      else return false;
    },
    showRetailUnitPrice() {
      return (
        this.showUnitPrice &&
        isOnDiscount(this.product) &&
        this.retailUnitPrice != null &&
        this.retailUnitPrice > 0 &&
        this.unitPrice != null &&
        this.retailUnitPrice > this.unitPrice
      );
    },

    showUnitPrice() {
      if (this.product.Type === 'Rental' && this.unitPrice < 0.005) {
        return false;
      }
      return true;
    },
    retailUnitPrice() {
      if (isWeightedProduct(this.product)) {
        if (hasGeneralDiscount(this.product)) {
          return getRetailOverviewPrice(this.product);
        }
        return getOverviewPrice(this.product);
      }

      if (hasGeneralDiscount(this.product)) {
        return getRetailUnitPrice(this.product);
      }
      return getUnitPrice(this.productSelection);
    },
    unitPrice() {
      if (isWeightedProduct(this.product)) {
        return this.getPriceForPersonalDiscount(getOverviewPrice(this.product));
      }

      return this.getPriceForPersonalDiscount(getUnitPrice(this.productSelection));
    },

    totalPrice() {
      if (applyPersonalDiscount(this.product)) {
        return this.getTotalPriceWithPersonalDiscount();
      }
      return this.getTotalPriceWithoutPersonalDiscount();
    },
    totalRetailPrice() {
      if (applyPersonalDiscount(this.product)) {
        return this.getTotalRetailPriceWithPersonalDiscount();
      }
      return this.getTotalRetailPriceWithoutPersonalDiscount();
    },
    totalPriceInclVat() {
      return this.getPriceForPersonalDiscount(
        getTotalPriceInclVAT(this.productSelection)
      );
    },
    increaseBtnDisabled() {
      return !quantityIncreasable(
        this.product,
        this.selectedItem,
        this.quantity
      );
    },
    decreaseBtnDisabled() {
      return !quantityDecreasable(
        this.product,
        this.selectedItem,
        this.quantity
      );
    },
    totalPriceTitle() {
      if (
        this.selectedItem != null ||
        (isWeightedProduct && this.product.WeightedInfo.FixedPrice)
      ) {
        return this.$translate('TOTAL_PRICE');
      }
      return `${this.$translate('PRICE_STARTING_AT')}:`;
    },
    productSelection() {
      return {
        Product: { ...this.product },
        ItemId: this.selectedItem.Id,
        Item: { ...this.selectedItem },
        Quantity: this.quantity,
      };
    },
    priceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    outOfStock() {
      return !hasCapacity(this.product, this.selectedItem);
    },
    getProductCount() {
      let quantity = 0;

      if (this.productsInCart.length < 0) this.initOrders();
      for (let i = 0; i < this.productsInCart.length; i++) {
        let obj = this.productsInCart[i];
        if (obj.ProductId === this.product.Id) {
          if (obj.ProductType !== 'Weighted') {
            quantity += Number(obj.Quantity);
          } else {
            quantity += 1;
          }
        }
      }
      return quantity;
    },
    orderBtnBgColor() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return 'transparent';
      }
      return '';
    },
  },

  methods: {
    //external methods
    hasCapacity,
    hasGeneralDiscount,
    isOnDiscount,
    personalDiscount,
    applyPersonalDiscount,
    isWeightedProduct,
    getUnitPrice,
    getRetailUnitPrice,
    getOverviewPrice,
    getRetailOverviewPrice,
    getDisplayMinQuantity,
    getDisplayQuantity,
    getDisplayUnit,
    getDisplayStepSize,
    getTotalPriceInclVAT,
    getVATText,
    getSortedItemDefNames,
    getSortedItemDefValues,

    init() {
      // Remove default item in case of ItemDefs configured
      if (
        this.product.ItemDefs != null &&
        Object.keys(this.product.ItemDefs).length > 0
      ) {
        removeDefaultItem(this.product);
      }

      this.initSelectedItemDefs();

      // Init quantity
      this.quantity = getQuantity(
        this.product,
        this.selectedItem,
        this.quantity
      );
    },
    initSelectedItemDefs() {
      // Preselect item defs
      this.selectedItemDefs = null;

      if (this.product.Items != null && this.product.Items.length > 0) {
        const cheapestItem = { ...this.product.Items[0] };
        this.selectedItem = { ...cheapestItem };
        this.selectedItemDefs = { ...cheapestItem.ItemDef };
      } else {
        this.selectedItem = {};
        this.selectedItemDefs = {};
      }
    },
    // increase qualtity of product
    increaseQuantity() {
      if (isWeightedProduct(this.product)) {
        console.log('weighted');
        this.quantity += this.product.WeightedInfo.StepSize;
      } else {
        this.quantity += 1;
        console.log('no weight', this.quantity);
      }
    },
    // decrease quantity of products
    decreaseQuantity() {
      if (isWeightedProduct(this.product)) {
        this.quantity -= this.product.WeightedInfo.StepSize;
      } else {
        this.quantity -= 1;
      }
    },
    // total unit price without personal discount
    getTotalPriceWithoutPersonalDiscount() {
      if (this.isProfUser) {
        return getTotalPriceExclVAT(this.productSelection);
      }
      return getTotalPriceInclVAT(this.productSelection);
    },

    // total unit price with personal discount
    getTotalPriceWithPersonalDiscount() {
      // Add a personal discount to current price
      const price = this.getTotalPriceWithoutPersonalDiscount();
      return this.getPriceForPersonalDiscount(price);
    },

    // total retail price without personal discount
    getTotalRetailPriceWithoutPersonalDiscount() {
      if (this.isProfUser) {
        return getTotalRetailPriceExclVAT(this.productSelection);
      }
      return getTotalRetailPriceInclVAT(this.productSelection);
    },
    //total retail price with personal discount
    getTotalRetailPriceWithPersonalDiscount() {
      // For retail price with a personal discount, the current price should be shown
      if (applyPersonalDiscount(this.productSelection)) {
        return this.getTotalPriceWithoutPersonalDiscount();
      }
      return null;
    },

    //check - called by unitPrice
    getPriceForPersonalDiscount(originalPrice) {
      let price = originalPrice;
      if (applyPersonalDiscount(this.product)) {
        price = utility.getDiscountPrice(
          originalPrice,
          personalDiscount(this.product)
        );
      }
      return price;
    },
    onSingleBookBtnClick(bookProps) {
      this.selectedShoppingList = { ...bookProps.ShoppingList };
      this.selectedItem = { ...bookProps.Item };
      this.quantity = bookProps.Quantity;

      /* For Rental product
     Go to event details page for rental product reservation */
      if (this.product.Type === 'Rental') {
        if (this.$route.name !== 'ProductWithCategory') {
          this.goToPage('ProductWithCategory', {
            event_id: this.product.EventId,
            category_id: this.product.Categories[0].Id,
            product_id: this.product.Id,
          });
        } else {
          this.checkProductListLength();
        }
        // to hide the menu if you go to an event for rental products
        window.EventBus.fire('hideSearchMenu');
        return;
      }
      // Non-rental product
      if (
        this.selectedShoppingList.ShoppingListId ===
        SHOPPING_CONTAINER.ShoppingCart
      ) {
        // Shopping cart selected
        this.checkTheProductAge(this.shoppingCartFlow);
      } else {
        this.checkTheProductAge(this.shoppingListFlow);
      }
    },
    checkTheProductAge(flow) {
      this.selectedFlow = flow;
      const lowestAge = 0;
      if (this.product != null && this.product.ForAge > lowestAge) {
        this.showAgeAlert = true;
        return;
      }

      if (flow === this.shoppingListFlow) {
        this.addToShoppingList();
      } else this.addToCart();
    },

    triggerShoppingListOrCard() {
      if (this.selectedFlow === this.shoppingListFlow) {
        this.addToShoppingList();
      } else {
        this.addToCart();
      }
    },

    addToShoppingList() {
      this.selectedFlow = '';
      this.showAgeAlert = false;

      let orderId = '';
      if (
        this.selectedShoppingList.OrderGroup != null &&
        this.selectedShoppingList.OrderGroup.Orders != null
      ) {
        const order = this.selectedShoppingList.OrderGroup.Orders.find(
          (orderInList) =>
            String(orderInList.EventId) === String(this.product.EventId)
        );
        if (order != null) {
          orderId = order.OrderId;
        }
      }

      let createList = true;
      let shoppingListId = '';
      if (
        this.selectedShoppingList != null &&
        this.selectedShoppingList.ShoppingListId != null &&
        this.selectedShoppingList.ShoppingListId !== '' &&
        this.selectedShoppingList.ShoppingListId !== -1
      ) {
        shoppingListId = this.selectedShoppingList.ShoppingListId;
        createList = false;
      }

      const payload = {
        CreateShoppingList: createList,
        ShoppingListName: this.selectedShoppingList.ShoppingListName,
        ShoppingListId: shoppingListId,
        OrderId: orderId,
        EventId: this.product.EventId,
        ProductId: this.product.Id,
        ItemId: this.selectedItem.Id,
        Quantity: this.quantity,
        OptionSelection: this.getProductOptions(),
      };

      addProductToReservation(payload)
        .then(() => {})
        .catch((error) => {
          if (error.data.ErrorCode === "E_SL_017") {
            this.alreadyDeletedList();
          }
        });

      this.$emit('back');
    },
    alreadyDeletedList() {
      this.$emit('getShoppingLists');
      window.EventBus.fire('activateSnackBar', {
        text: this.$translate('SHOPPING_LIST_NO_LONGER_EXISTS'),
        color: 'error',
      });
      this.deleteShoppingListDialog = false;
    },
    addToCart() {
      this.selectedFlow = '';
      this.showAgeAlert = false;

      const productInfo = this.getProductForReserve();
      this.reserve(productInfo);
    },
    getProductForReserve() {
      const prodInfo = {
        EventType: this.product.EventType,
        EventId: this.product.EventId,
        Name: this.product.Name,
        Picture: this.product.Picture,
        Id: this.product.Id,
        ItemId: this.selectedItem.Id,
        ItemDefs: this.selectedItem.ItemDef,
        Type: this.product.Type,
        Price: this.selectedItem.Price,
        Quantity: this.quantity,
        OptionSelection: this.getProductOptions(),
        Product: this.product,
        Item: this.selectedItem,
      };
      if (isWeightedProduct(this.product)) {
        // Add extra info for Weighted product
        prodInfo.Price =
          (this.quantity / this.product.WeightedInfo.StepSize) *
          this.product.WeightedInfo.PricePerStep;
        prodInfo.Amount = this.quantity;
        prodInfo.Quantity = 1;
        prodInfo.FixedPrice = this.product.WeightedInfo.FixedPrice;
        prodInfo.Unit = this.product.WeightedInfo.Unit;
        prodInfo.WeightedInfo = this.product.WeightedInfo;
      }

      return prodInfo;
    },
    getProductOptions() {
      if (
        this.product != null &&
        this.product['OptionLists'] != null &&
        this.product['OptionLists'][0] != null &&
        this.product['OptionLists'][0]['OptionGroups'] != null &&
        this.product['OptionLists'][0]['OptionGroups'][0] != null &&
        this.product['OptionLists'][0]['OptionGroups'][0]['Options'] != null
      ) {
        let options =
          this.product['OptionLists'][0]['OptionGroups'][0]['Options'];
        let optionSelection = [];
        for (const key in options) {
          let quantity =
            options[key].MaxQuantity > 1 || options[key].MaxQuantity == -1
              ? options[key].Quantity
              : options[key].Selected
              ? 1
              : 0;
          if (quantity > 0) {
            let optSelection = {
              OptionId: options[key].OptionId,
              OptionName: options[key].OptionName,
              Quantity: quantity,
              TextResponse: '',
              PictureResponse: '',
            };
            optionSelection.push(optSelection);
          }
        }
        return optionSelection;
      }
      return [];
    },
    reserve(cartProduct) {
      this.$store.commit('shoppingCart/addOrder', cartProduct);
    },
    onBookBtnClick(selectedList) {
      let saveList = {
        ShoppingList: selectedList,
        Product: this.product,
        Item: this.selectedItem,
        Quantity: this.quantity,
      };
      this.onSingleBookBtnClick(saveList);
    },
    goToLogin() {
      // todo
    },
    onItemDefChange() {
      // For the purpose of triggering reactivity to recompute selectedItem
      const defs = { ...this.selectedItemDefs };
      this.selectedItemDefs = {};
      this.selectedItemDefs = { ...defs };
      this.selectedItem = {};
      this.selectedItem = { ...this.getSelectedItem() };
      this.quantity = getQuantity(
        this.product,
        this.selectedItem,
        this.quantity
      );
    },
    getSelectedItem() {
      if (
        this.product == null ||
        this.product.Items == null ||
        this.product.Items.length === 0
      ) {
        // Empty product
        return null;
      }

      if (this.product.Items.length === 1) {
        // Only one item (no variations)
        return this.product.Items[0];
      }

      // Has variations, return the one matches user selections
      if (
        this.selectedItemDefs == null ||
        Object.keys(this.selectedItemDefs).length === 0 ||
        Object.keys(this.selectedItemDefs).length !==
          Object.keys(this.product.ItemDefs).length
      ) {
        // No variations selected or selection not completed yet
        return null;
      }

      // Selection of variations completed, get matched item
      return this.product.Items.find((item) => {
        let variationMatch = true;

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(this.selectedItemDefs)) {
          if (item.ItemDef[key] == null || item.ItemDef[key] !== value) {
            // Not match, stop looping
            variationMatch = false;
            break;
          }
        }

        return variationMatch;
      });
    },

    initOrders() {
      this.shoppingCartCounter = [];
      if (this.orders.length > 0) {
        this.shoppingCartCounter = cloneDeep(this.orders);
      } else {
        this.productsInCart = [];
      }
      if (this.shoppingCartCounter.length > 0) {
        this.productsInCart = this.shoppingCartCounter[0].Products;
      }
    },
  },
};
</script>
<style>
.add-btn {
  position: absolute;
  top: 15px;
  right: 10px;
}
.sold-out-label-box {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background-color: rgba(192, 192, 192, 0.5);
}
.sold-out-label-text {
  position: absolute;
  top: 0;
  left: 0;
  display: inline;
  background-color: #ff3333;
  color: white;
  font-weight: bold;
  font-size: 14px;
  padding: 4px 8px;
}
</style>
